import { Link } from "react-router-dom";
import { SEOPageLayout, InlineCTA, FAQList } from "../../components/seo/SEOPageLayout";

const FAQS = [
  { q: "How long does the process take?", a: "Most sellers get an eligibility answer within a few hours of texting a photo. If the item qualifies, a local meetup can usually be arranged the same day or the next day depending on both of our schedules." },
  { q: "Do I need to clean or prepare the boxes?", a: "No preparation is required. Do not open, tape over, or remove any part of the retail packaging. We prefer boxes exactly as they came from the pharmacy." },
  { q: "What if I only have one or two boxes?", a: "One or two eligible boxes is still worth reviewing. Text us a photo and we will let you know either way. We do not have a minimum order size for local meetups." },
  { q: "Do you need my ID at the meetup?", a: "For any legitimate cash transaction we recommend both parties feel comfortable identifying themselves. We can discuss specifics before the meetup so there are no surprises." },
];

export default function HowToSell() {
  return (
    <SEOPageLayout
      title="How to Sell Unused Diabetic Test Strips | Step-by-Step Guide"
      description="A step-by-step guide to selling unused diabetic test strips. Learn how eligibility works, what photos to send, and how the local process runs in Jacksonville."
      canonical="https://cashforteststripsjacksonville.com/how-to-sell-unused-diabetic-test-strips/"
      breadcrumbLabel="How to Sell Unused Test Strips"
      primaryKeyword="How to Sell Unused Diabetic Test Strips"
      h1="How to Sell Unused Diabetic Test Strips"
      intro="This is a plain-language walkthrough of what to do if you have extra, unopened diabetic test strips at home. There are no gimmicks and no upfront promises about price — just a clear four-step process so you know what to expect."
      faqs={FAQS}
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-step1">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Step 1: Check if your supplies may be eligible</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              Before you contact anyone, look over the boxes and confirm the basics: the outer packaging is factory-sealed and undamaged, the printed expiration date is at least six months away, and the box is not stamped Medicaid or Medicare. If those three things check out, your box has a real chance of being eligible.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              You can also cross-reference the brand against our{" "}
              <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link>. If your brand is not listed there, still ask us — the list evolves.
            </p>
          </section>

          <section data-testid="section-step2">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Step 2: Review what we accept</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Take a minute to skim what we accept, so you know we can actually help. We currently buy eligible items across Dexcom, FreeStyle Libre, Omnipod, Medtronic, Tandem, and test strips from Accu-Chek, OneTouch, Contour Next, ReliOn, True Metrix, and FreeStyle. See{" "}
              <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies</Link>{" "}
              for the complete breakdown.
            </p>
          </section>

          <section data-testid="section-step3">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Step 3: Contact us with photos</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              This is the important part. Take two clear, well-lit photos of each box: one of the front (showing the brand and product code) and one of the back or side (showing the expiration date). Send them to <a href="tel:+19049559646" className="text-[#0F5132] font-semibold underline">(904) 955-9646</a> by text, or upload them through our Get Started form.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              We reply with either an eligibility confirmation, a specific quote, or a straightforward explanation of why the item does not qualify. You do not have to guess.
            </p>
          </section>

          <section data-testid="section-step4">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Step 4: Follow the instructions for the meetup</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              If your supplies qualify, we pick a safe, public location in Jacksonville or a nearby area at a time that works for both of us. We inspect the supplies in front of you and pay same-day cash. No shipping, no signup, no waiting on a bank transfer.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              For a broader look at the service, read the main{" "}
              <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">sell diabetic test strips</Link>{" "}
              guide.
            </p>
          </section>

          <section data-testid="section-tips">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Tips to Make the Process Smooth</h2>
            <ul className="space-y-2 text-lg text-[#4B5563] list-disc list-inside">
              <li>Keep boxes in a cool, dry place — never in a hot car or bathroom.</li>
              <li>Group multiple boxes together in a single message rather than one at a time.</li>
              <li>Include the printed expiration date in your first message to save a round-trip.</li>
              <li>Do not remove or write on the outer packaging.</li>
            </ul>
          </section>

          <section data-testid="section-faq">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>Frequently Asked Questions</h2>
            <FAQList faqs={FAQS} />
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Have Your Photos Ready?"
            sub="Send them to us and we will handle the rest — no obligation to sell."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
